<script setup lang="ts">
import { RouterView, RouterLink, useRoute, useRouter } from 'vue-router'
import { computed, ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { useChatStore } from '@/stores/chatStore'
import { ToastContainer } from '@/components/ui'
import ErrorBoundary from '@/components/ErrorBoundary.vue'
import TourGuide from '@/components/onboarding/TourGuide.vue'
import '@/styles/design-tokens.css'

const auth = useAuthStore()
const router = useRouter()
const chatStore = useChatStore()
const hasUnreadChats = computed(() => chatStore.unreadChatIds.length > 0)

// Mobile navigation state
const mobileNavOpen = ref(false)

// Desktop sidebar collapse
const sidebarCollapsed = ref(false)
const tourRef = ref<InstanceType<typeof TourGuide> | null>(null)

const startTour = () => {
  tourRef.value?.start()
}

const handleDrawerKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    mobileNavOpen.value = false
    return
  }
  if (e.key === 'Tab' && e.target instanceof HTMLElement) {
    const drawer = e.currentTarget as HTMLElement
    const focusable = drawer.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    if (focusable.length === 0) {
      e.preventDefault()
      return
    }
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }
}

// Breadcrumbs
const route = useRoute()

const navLinks = [
  { path: '/', name: 'Чат', icon: 'chat', requiresAuth: true },
  { path: '/faq', name: 'Частые вопросы', icon: 'helpCircle', requiresAuth: true },
  { path: '/announcements', name: 'Объявления', icon: 'megaphone', requiresAuth: true },
  { path: '/broadcast', name: 'Рассылки', icon: 'broadcast', requiresAuth: true, requiresManager: true },
  { path: '/support-queue', name: 'Обращения', icon: 'admin', requiresAuth: true, requiresManager: true },
  { path: '/documents', name: 'Документы', icon: 'documents', requiresAuth: true, requiresAdmin: true },
  { path: '/analytics', name: 'Аналитика', icon: 'analytics', requiresAuth: true, requiresAdmin: true },
  { path: '/admin', name: 'Админ-панель', icon: 'admin', requiresAuth: true, requiresAdmin: true },
  { path: '/profile', name: 'Профиль', icon: 'profile', requiresAuth: true },
]
onMounted(() => {
  // TourGuide — только по кнопке в панели навигации, без автостарта
  // Первый онбординг (OnboardingTooltip) запускается в ChatPage
  // Повторный запуск из профиля («Показать обучение ещё раз»)
  window.addEventListener('mis:start-tour', startTour)
})

const breadcrumbs = computed(() =>
  route.matched.filter(r => r.meta?.title).map(r => ({ text: r.meta!.title as string, href: r.path }))
)

const filteredNavLinks = computed(() =>
  navLinks.filter(link => {
    // Not authenticated — nothing shown (login button at bottom instead)
    if (!auth.isAuthenticated) return false
    // Authenticated non-admin — only chat
    if (!auth.isManager && link.requiresManager) return false
    if (!auth.isAdmin) return !link.requiresAdmin
    return true
  })
)

// SVG icon components for navigation
const navIcons: Record<string, string> = {
  chat: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
  dashboard: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  documents: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  analytics: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  admin: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  broadcast: 'M2.5 21l2.5-9 9-2.5-9-2.5-2.5-9 21 12-21 12z',
  profile: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  helpCircle: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z',
  megaphone: 'M3 11v3l4 1 2 5h2l-1.5-5.5L19 17V7L9 10l-6 1zm16 0v3m1.5-5.5a4 4 0 010 7',
  download: 'M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
}

// `auth.logout()` асинхронный: токен чистится в его `finally`, уже после
// ответа сети. Навигация без `await` уезжает на `/login` ДО сброса токена,
// и гвард роутера (router.ts:115) видит пользователя авторизованным —
// отправляет обратно в чат. Симптом: кнопка в сайдбаре стала «Войти», но
// чат на экране остался.
const handleLogout = async () => {
  await auth.logout()
  await router.push('/login')
}
</script>

<template>
  <div class="min-h-screen flex" style="background: var(--bg-main);">
    <!-- Sidebar -->
    <aside
      :class="[
        'glass-sidebar flex-shrink-0 hidden md:flex flex-col z-10 transition-all duration-300',
        sidebarCollapsed ? 'w-16' : 'w-64'
      ]"
      aria-label="Навигация по сайту"
    >
      <!-- Logo + Collapse toggle -->
      <div data-tour="welcome" class="p-4 flex items-center" :class="sidebarCollapsed ? 'justify-center' : 'justify-between'" style="border-bottom: 1px solid var(--glass-border);">
        <div v-if="!sidebarCollapsed">
          <h1 class="text-lg font-semibold" style="color: var(--color-primary); font-size: 1.25rem;">Ассистент</h1>
          <p class="text-xs text-muted mt-0.5">Интеллектуальный помощник</p>
        </div>
        <button
          @click="sidebarCollapsed = !sidebarCollapsed"
          class="p-1.5 rounded transition-colors hover:bg-glass-bg-hover"
          :aria-label="sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'"
          :title="sidebarCollapsed ? 'Развернуть' : 'Свернуть'"
        >
          <svg class="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path v-if="sidebarCollapsed" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      <!-- Navigation -->
      <nav data-tour="sidebar-nav" class="flex-1 p-3 space-y-1" role="list">
        <RouterLink
          v-for="link in filteredNavLinks"
          :key="link.path"
          :to="link.path"
          role="listitem"
          class="flex items-center gap-3 rounded font-medium transition-all duration-200"
          :class="[
            sidebarCollapsed ? 'justify-center px-2 py-2.5' : 'px-4 py-2.5',
            $route.path !== link.path ? 'hover:bg-glass-bg-hover' : '',
          ]"
          :style="{
            fontSize: '0.875rem',
            color: $route.path === link.path ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            background: $route.path === link.path ? 'var(--color-primary-muted)' : 'transparent',
            border: $route.path === link.path ? '1px solid var(--glass-border)' : '1px solid transparent',
          }"
          :title="sidebarCollapsed ? link.name : undefined"
          :aria-current="$route.path === link.path ? 'page' : undefined"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="flex-shrink: 0;">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" :d="navIcons[link.icon]" />
          </svg>
          <span
            v-if="link.path === '/' && hasUnreadChats"
            class="w-2 h-2 rounded-full"
            style="background: var(--color-primary); flex-shrink: 0;"
            aria-label="Есть непрочитанные сообщения"
          ></span>
          <span v-if="!sidebarCollapsed">{{ link.name }}</span>
        </RouterLink>
      </nav>

      <!-- User section -->
      <div :class="sidebarCollapsed ? 'p-2' : 'p-3 sm:p-4'">
        <div v-if="auth.user" class="flex items-center gap-3 p-2 rounded transition-colors hover:bg-glass-bg-hover" :class="sidebarCollapsed ? 'justify-center' : ''">
          <span class="inline-flex h-8 w-8 rounded items-center justify-center text-xs font-semibold text-white flex-shrink-0" style="background: var(--color-primary);">
            {{ (auth.user.name || 'U')[0].toUpperCase() }}
          </span>
          <div v-if="!sidebarCollapsed" class="flex-1 min-w-0">
            <p class="text-sm font-medium truncate text-default">{{ auth.user.name || 'Пользователь' }}</p>
            <p class="text-xs truncate text-muted">{{ auth.user.email || '' }}</p>
          </div>
        </div>
        <RouterLink v-else to="/login" class="block text-center py-2 px-4 rounded font-medium glass-card" style="color: var(--color-primary);">
          <span v-if="!sidebarCollapsed">Войти</span>
          <svg v-else class="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
          </svg>
        </RouterLink>
        <button
          v-if="auth.user"
          @click="handleLogout"
          class="mt-2 w-full py-2 text-sm rounded transition-colors text-muted"
          :class="sidebarCollapsed ? 'px-0' : ''"
          :title="sidebarCollapsed ? 'Выйти' : undefined"
        >
          <span v-if="!sidebarCollapsed">Выйти</span>
          <svg v-else class="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>
      </div>
    </aside>

    <!-- Main content area -->
    <div class="flex-1 flex flex-col min-w-0">
      <!-- Mobile header -->
      <header class="md:hidden glass-card border-x-0 border-t-0 p-4 flex items-center justify-between" style="border-radius: 0;">
        <h1 class="text-lg font-bold" style="color: var(--color-primary);">Ассистент</h1>
        <button
          aria-label="Открыть меню"
          @click="mobileNavOpen = !mobileNavOpen"
          class="p-2 rounded glass-card"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--color-text-secondary);">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </header>

      <!-- Mobile navigation drawer -->
      <Transition name="slide">
        <div
          v-if="mobileNavOpen"
          role="dialog"
          aria-modal="true"
          aria-label="Навигация"
          class="md:hidden fixed inset-0 z-50 glass-strong"
          style="border-radius: 0;"
          @keydown="handleDrawerKeydown"
        >
        <div class="p-4 flex justify-between items-center" style="border-bottom: 1px solid var(--glass-border);">
          <h2 class="text-lg font-bold" style="color: var(--color-primary);">Меню</h2>
          <button @click="mobileNavOpen = false" aria-label="Закрыть меню" class="p-2 rounded hover:bg-glass-bg-hover text-muted">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav class="p-4 space-y-2">
          <RouterLink
            v-for="link in filteredNavLinks"
            :key="link.path"
            :to="link.path"
            class="flex items-center gap-3 px-4 py-3 rounded font-medium transition-all"
            :style="{
              color: $route.path === link.path ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              background: $route.path === link.path ? 'var(--color-primary-muted)' : 'transparent',
              border: $route.path === link.path ? '1px solid var(--glass-border)' : '1px solid transparent',
            }"
            @click="mobileNavOpen = false"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" :d="navIcons[link.icon]" />
            </svg>
            <span
              v-if="link.path === '/' && hasUnreadChats"
              class="w-2 h-2 rounded-full"
              style="background: var(--color-primary); flex-shrink: 0;"
              aria-label="Есть непрочитанные сообщения"
            ></span>
            <span>{{ link.name }}</span>
          </RouterLink>
          <!-- Mobile download app link -->
          <a
            href="/app.apk"
            download
            class="flex items-center gap-3 w-full px-4 py-3 rounded font-medium transition-all mt-3"
            style="color: var(--color-primary); background: transparent; border: 1px solid var(--glass-border);"
            @click="mobileNavOpen = false"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" :d="navIcons['download']" />
            </svg>
            <span>Скачать приложение</span>
          </a>
          <!-- Mobile logout -->
          <button
            v-if="auth.user"
            @click="handleLogout(); mobileNavOpen = false"
            class="flex items-center gap-3 w-full px-4 py-3 rounded font-medium transition-all mt-2"
            style="color: var(--color-text-muted); background: transparent; border: 1px solid var(--glass-border);"
          >
            Выйти
          </button>
        </nav>
      </div>
      </Transition>

      <!-- Breadcrumbs -->
      <div v-if="breadcrumbs.length" class="hidden sm:flex px-6 py-3 items-center">
        <nav v-if="breadcrumbs.length" class="text-sm text-muted" aria-label="Breadcrumb">
          <span v-for="(b, idx) in breadcrumbs" :key="idx">
            <RouterLink v-if="idx < breadcrumbs.length - 1" :to="b.href" class="transition-colors text-muted">{{ b.text }}</RouterLink>
            <span v-else aria-current="page" style="color: var(--color-text-secondary);">{{ b.text }}</span>
            <span v-if="idx < breadcrumbs.length - 1" class="mx-2" style="color: var(--border-color);">/</span>
          </span>
        </nav>
      </div>

      <!-- Main Content -->
      <main class="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
        <ErrorBoundary>
          <RouterView />
        </ErrorBoundary>
      </main>

      <!-- Footer -->
      <footer class="p-3 text-center text-xs" style="color: var(--color-text-muted); border-top: 1px solid var(--glass-border); background: var(--glass-bg-sidebar);">
        <div>
          Ассистент &copy; {{ new Date().getFullYear() }} · Все права защищены
          <span aria-hidden="true"> · </span>
          <a
            href="/app.apk"
            download
            class="inline-block py-2 px-1"
            style="color: var(--color-primary);"
            aria-label="Скачать мобильное приложение"
          >Скачать приложение</a>
        </div>
        <div class="mt-1" style="font-size: 0.6875rem; opacity: 0.75;">
          Проприетарное программное обеспечение. Копирование, распространение и использование без разрешения правообладателя запрещены.
        </div>
      </footer>
    </div>

    <!-- Tour Guide -->
    <TourGuide ref="tourRef" @close="() => {}" />

    <!-- Toast Container -->
    <ToastContainer />
  </div>
</template>

<style>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
